<template>
  <div class="flex items-center justify-between mt-4 mb-8 text-xs md:text-sm">
    <div class="flex gap-1 md:gap-2 items-center m-auto">
      <!-- First page -->
      <RouterLink
        v-if="useRouter"
        :to="getPageLink(1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage === 1 }
        ]"
      >
        <ChevronsLeft class="w-4 h-4" />
      </RouterLink>
      <button
        v-else
        @click="$emit('update:page', 1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage === 1 }
        ]"
      >
        <ChevronsLeft class="w-4 h-4" />
      </button>

      <!-- Previous page -->
      <RouterLink
        v-if="useRouter"
        :to="getPageLink(currentPage - 1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage === 1 }
        ]"
      >
        <ChevronLeft class="w-4 h-4" />
      </RouterLink>
      <button
        v-else
        @click="$emit('update:page', currentPage - 1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage === 1 }
        ]"
      >
        <ChevronLeft class="w-4 h-4" />
      </button>

      <!-- Page numbers -->
      <div class="flex md:gap-1">
        <template v-if="useRouter">
          <RouterLink
            v-for="pageNum in visiblePages"
            :key="`router-${pageNum}`"
            :to="getPageLink(pageNum)"
            :class="[
              'w-8 h-8 rounded-full flex items-center justify-center',
              currentPage === pageNum ? 'bg-cosmicPurple text-white' : 'hover:bg-gray-50'
            ]"
          >
            {{ pageNum }}
          </RouterLink>
        </template>
        <template v-else>
          <button
            v-for="pageNum in visiblePages"
            :key="`button-${pageNum}`"
            @click="$emit('update:page', pageNum)"
            :class="[
              'w-8 h-8 rounded-full flex items-center justify-center',
              currentPage === pageNum ? 'bg-cosmicPurple text-white' : 'hover:bg-gray-50'
            ]"
          >
            {{ pageNum }}
          </button>
        </template>
      </div>

      <!-- Next page -->
      <RouterLink
        v-if="useRouter"
        :to="getPageLink(currentPage + 1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage >= totalPages }
        ]"
      >
        <ChevronRight class="w-4 h-4" />
      </RouterLink>
      <button
        v-else
        @click="$emit('update:page', currentPage + 1)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage >= totalPages }
        ]"
      >
        <ChevronRight class="w-4 h-4" />
      </button>

      <!-- Last page -->
      <RouterLink
        v-if="useRouter"
        :to="getPageLink(totalPages)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage >= totalPages }
        ]"
      >
        <ChevronsRight class="w-4 h-4" />
      </RouterLink>
      <button
        v-else
        @click="$emit('update:page', totalPages)"
        :class="[
          'px-3 py-1 rounded hover:bg-gray-50',
          { 'pointer-events-none opacity-50': currentPage >= totalPages }
        ]"
      >
        <ChevronsRight class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-vue-next'
import { useRoute } from 'vue-router'

const route = useRoute()

const props = defineProps<{
  currentPage: number
  totalPages: number
  useRouter?: boolean
  additionalQueryParams?: Record<string, any>
}>()

defineEmits<{
  'update:page': [page: number]
}>()

const visiblePages = computed(() => {
  const total = props.totalPages
  const current = props.currentPage
  const delta = 2 // Number of pages to show on each side

  let start = Math.max(current - delta, 1)
  let end = Math.min(start + 4, total) // Always show 5 pages

  // If we're near the end, adjust the start
  if (end - start < 4) {
    start = Math.max(end - 4, 1)
  }

  // If we're near the start, adjust the end
  if (end - start < 4) {
    end = Math.min(start + 4, total)
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
})

const getPageLink = (page: number) => ({
  query: {
    ...route.query,
    ...props.additionalQueryParams,
    page: page.toString()
  }
})
</script> 